import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import {
  Button,
  Checkbox,
  OutlineCard,
  Select,
  Switch,
  useToast
} from '@morressier/alembic';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import {
  fetchOrgArticleTypes,
  fetchSubmissionContext,
  fetchSubmissionContextArticleTypes
} from 'src/api/queries';
import { Layout } from 'src/components/Layout';
import { Overlay } from 'src/components/Overlay';
import { formFieldSpecifications } from '@publishing-engine/submission-service/src/fixtures/form-field-specifications';
import { createArticleType } from 'src/api/commands';
import { FormFieldSpecification } from '@publishing-engine/domain/types/article';
import { objFilter } from 'src/utils/object';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useUpdateJournalSettings } from 'src/api/mutations';
import PeerReviewModelSelect from './Configure/PeerReviewModelSelect';
import {
  buildPeerReviewModelFieldConfig,
  PEER_REVIEW_MODEL_OPTIONS
} from './Configure/GeneralInfo/peerReviewModelConfig';
import { useWorkspace } from '../../../SubmissionContexts/Users/userHook';

const CreateJournalArticleTypes = () => {
  const { handleSubmit, control, register, reset, watch } = useForm({
    defaultValues: { articleTypeId: '', fields: formFieldSpecifications }
  });
  const navigate = useNavigate();
  const toast = useToast();
  const { journalId = '' } = useParams();
  const [peerReviewModel, setPeerReviewModel] = useState('');

  const updateSettingMutation = useUpdateJournalSettings(journalId);
  const queryClient = useQueryClient();

  /**
   * The model is stored per article workflow, so it is written once the article
   * type has been created and the journal read back tells us which workflow that
   * added. If the write fails the article type still exists, without a model —
   * which the Article Types tab shows and lets an admin fill in.
   */
  const savePeerReviewModel = async (articleTypeId: string) => {
    const updatedJournal = await queryClient.fetchQuery({
      queryKey: ['submission-context', journalId],
      queryFn: () => fetchSubmissionContext(journalId)
    });

    const articleWorkflowId = updatedJournal?.articleWorkflows?.[articleTypeId];

    if (!articleWorkflowId) return false;

    const response = await updateSettingMutation.mutateAsync(
      buildPeerReviewModelFieldConfig(articleWorkflowId).buildPayload(
        peerReviewModel,
        updatedJournal
      )
    );

    return response.isSuccess;
  };

  const onSubmit = async (data: {
    articleTypeId: string;
    fields: Record<string, FormFieldSpecification>;
  }) => {
    if (!journalId) throw new Error('no journalId');

    if (!peerReviewModel) {
      toast({
        variant: 'error',
        title: 'Please select a peer review model'
      });
      return;
    }

    const filteredFields = objFilter(
      data.fields,
      (_k, v) => v.isEnabled.value
    ) as Record<string, FormFieldSpecification>;

    try {
      await createArticleType(journalId, { ...data, fields: filteredFields });

      const isModelSaved = await savePeerReviewModel(data.articleTypeId);

      if (!isModelSaved) {
        toast({
          variant: 'error',
          title:
            'Article created, but its peer review model could not be saved. Set it from the article type’s row.'
        });
        onClose();
        return;
      }

      toast({
        variant: 'success',
        title: `Article created`
      });
      onClose();
    } catch (e: any) {
      toast({
        variant: 'error',
        title: e.message
      });
    } finally {
      reset();
    }
  };

  const { orgId, workspaceShortname } = useWorkspace();

  const { data: organizationArticleTypes } = useQuery({
    queryKey: ['organization-article-types', { orgId }],
    queryFn: () => fetchOrgArticleTypes(orgId),
    enabled: !!orgId
  });
  const { data: journalArticleTypes = { articleTypes: [] } } = useQuery({
    queryKey: ['journalArticleTypes', journalId],
    queryFn: () => fetchSubmissionContextArticleTypes(journalId),
    enabled: !!journalId
  });

  const orgArticleTypes = organizationArticleTypes?.items;
  const existingJournalArticleTypes = new Set(
    journalArticleTypes.articleTypes.map(at => at.id)
  );

  const onClose = () => {
    navigate(
      `/app/workspace/${workspaceShortname}/manage/journals/${journalId}/edit`
    );
  };

  const renderGroup = ({
    title,
    subTitle = '',
    footerText = '',
    fields,
    visibility = 'visible'
  }: {
    title: string;
    subTitle?: string;
    footerText?: string;
    fields: (keyof typeof formFieldSpecifications)[];
    visibility?: 'hidden' | 'visible';
  }) => {
    return (
      <OutlineCard
        borderless
        visibility={visibility}
        height={visibility === 'visible' ? 'auto' : 0}
        overflow={visibility === 'visible' ? 'visible' : 'hidden'}
      >
        <Flex padding={8} gap={8} flexDirection="column">
          <Flex flexDirection="column" width="100%">
            <Flex alignItems="center" justifyContent="space-between">
              <Text textStyle="headingGroup">{title}</Text>
              <Tooltip hasArrow placement="top" label="A required message">
                <Box>
                  {/* TODO: figure out what do with this logic */}
                  {/* <Switch {...register('enableSubmissions')} /> */}
                </Box>
              </Tooltip>
            </Flex>
            <Text>{subTitle}</Text>
          </Flex>

          <Flex flexDirection="column">
            <Flex textStyle="headingItem" gap={1}>
              <Flex width="120px">Status</Flex>
              <Flex width="150px">Title</Flex>
              <Flex flex="1">Description</Flex>
              <Flex width="150px">Required</Flex>
            </Flex>

            {fields.map(key => {
              const field = formFieldSpecifications[key];
              if (!field) return null;

              const isEnabled = watch(`fields.${key}.isEnabled.value`);
              return (
                <Flex
                  key={key}
                  alignItems="flex-start"
                  paddingY={6}
                  borderBottomWidth="1px"
                  borderColor="neutralPrimaryBorder"
                  borderStyle="solid"
                  _last={{ borderBottomWidth: 0 }}
                  gap={2}
                >
                  <Flex width="120px" gap="2" alignItems="center">
                    <Controller
                      name={`fields.${key}.isEnabled.value`}
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <Switch
                          isChecked={value}
                          onChange={onChange}
                          isDisabled={!field?.isEnabled.isEditable}
                        />
                      )}
                    />
                    Active
                  </Flex>
                  <Flex width="150px">{field.params.labels.main.value}</Flex>
                  <Flex flex="1">{field.params.labels.subtitle.value}</Flex>
                  <Flex width="150px">
                    <Controller
                      name={`fields.${key}.params.validations.required.value`}
                      control={control}
                      render={({ field: { onChange, value, name } }) => (
                        <Checkbox
                          name={name}
                          ariaLabel={`Is ${field.params.labels.main.value} required`}
                          id={field.name}
                          isChecked={value}
                          onChange={onChange}
                          isDisabled={
                            !field.params.validations.required.isEditable ||
                            !isEnabled
                          }
                        >
                          Is Required?
                        </Checkbox>
                      )}
                    />
                  </Flex>
                </Flex>
              );
            })}

            <Text
              textStyle="textContext"
              color="neutralSecondaryContent"
              marginTop={2}
            >
              {footerText}
            </Text>
          </Flex>
        </Flex>
      </OutlineCard>
    );
  };

  return (
    <Overlay>
      <Layout width="960px">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Flex flexDirection="column" gap={8}>
            <Flex>
              <Text textStyle="headingTitle">Add Article Type to Journal</Text>
              <Flex marginLeft="auto" gap={2} alignItems="center"></Flex>
            </Flex>

            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingGroup">Select Article Type</Text>
              <Select
                placeholder="Select article type"
                width="240px"
                {...register('articleTypeId')}
              >
                {orgArticleTypes?.map(type => (
                  <option
                    key={type.id}
                    value={type.id}
                    disabled={existingJournalArticleTypes.has(type.id)}
                  >
                    {type.name}
                  </option>
                ))}
              </Select>
            </Flex>

            {/* The next step after choosing the article type: the journal is live,
                so the new article type has to go live with a model chosen. */}
            <PeerReviewModelSelect
              value={peerReviewModel}
              options={PEER_REVIEW_MODEL_OPTIONS}
              onChange={setPeerReviewModel}
            />

            <Text textStyle="headingGroup">Configure Article Collection</Text>

            {/* ---- Submissions ---- */}
            {renderGroup({
              title: 'Submissions',
              fields: ['Title', 'Abstract', 'Manuscript', 'Keywords']
            })}

            {renderGroup({
              title: 'Authors',
              fields: [
                'SubmittingAuthor',
                'Authors',
                'ContactAuthor',
                'CorrespondingAuthor'
              ]
            })}

            {renderGroup({
              title: 'Background',
              fields: ['CoverLetter', 'NoveltyStatement']
            })}

            {renderGroup({
              title: 'Ethics',
              fields: ['EthicsStatement', 'ClinicalTrials']
            })}

            {renderGroup({
              title: 'Funding',
              fields: ['Funding']
            })}

            {renderGroup({
              title: 'Licenses & Permissions',
              fields: ['FiguresAndTables', 'LicensesAndPermissions']
            })}

            {renderGroup({
              title: 'Peer Review',
              fields: [
                'TransparentPeerReview',
                'DoubleAnonymousPeerReview',
                'RecommendedReviewers',
                'OpposedReviewers'
              ]
            })}

            {renderGroup({
              title: 'Publishing Preferences',
              fields: [
                'PublishingModel',
                'PreferenceImmediatePublication',
                'DataAvailability',
                'AcceleratedPublication',
                'IPEMMembership',
                'SubjectArea'
              ]
            })}

            {renderGroup({
              title: 'Hidden  Fields',
              fields: ['Supplementary', 'AnonymizedManuscript'],
              visibility: 'hidden'
            })}

            <Flex justifyContent="flex-end" mt={8} gap={4}>
              <Button variant="secondary" onClick={onClose}>
                Exit Without Saving
              </Button>
              <Button
                type="submit"
                variant="primary"
                isDisabled={!peerReviewModel}
              >
                Save Changes
              </Button>
            </Flex>
          </Flex>
        </form>
      </Layout>
    </Overlay>
  );
};

export default CreateJournalArticleTypes;
